import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import * as NomenclaturesActions from "./nomenclature.actions";
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {Nomenclature, NomenclatureSearchParams} from '@accounting/_common/models/nomenclature.models';
import {CategoryListResponse} from '@common/components-standalone/reference-category-module/category.type';
import {CategoriesTreeState, CategorizedEntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const nomenclatureFeatureKey = 'nomenclature';

export function sortEntities(a: Nomenclature, b: Nomenclature) {
    return a.name!.localeCompare(b.name!);
}

export function sortCategories(a: CategoryListResponse, b: CategoryListResponse) {
    return a.category.name.localeCompare(b.category.name);
}

export const adapter: EntityAdapter<Nomenclature> = createEntityAdapter<Nomenclature>({
    sortComparer: sortEntities
});

export const categoriesAdapter: EntityAdapter<CategoryListResponse> = createEntityAdapter<CategoryListResponse>({
    selectId: (category) => category.category.id!,
    sortComparer: sortCategories
});

export type NomenclatureState = CategorizedEntityStateModel<Nomenclature, NomenclatureSearchParams>;

export const initialState: NomenclatureState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: [],
    entityCategories: categoriesAdapter.getInitialState(),
    categoriesTreeState: {
        opened: {'default': true}
    }
});

export const nomenclatureReducer = createReducer(
    initialState,

    on(NomenclaturesActions.SetCategoriesState, (state, action) => ({
        ...state,
        categoriesTreeState: action.payload
    })),

    on(NomenclaturesActions.LoadCategoriesSuccess, (state, action) => ({
        ...state,
        entityCategories: categoriesAdapter.setAll(action.payload, {...state.entityCategories})
    })),

    on(NomenclaturesActions.UpsertCategory, (state, action) => ({
        ...state,
        entityCategories: categoriesAdapter.setOne(action.payload, {...state.entityCategories})
    })),

    on(NomenclaturesActions.DeleteCategory, (state, action) => ({
        ...state,
        entityCategories: categoriesAdapter.removeOne(action.payload.id!, {...state.entityCategories})
    })),

    on(NomenclaturesActions.CheckState, (state, action) => {
        if (action.payload.modalView) {
            return adapter.removeAll({
                ...state,
                savedFilter: !action.payload.close && state.filter ? state.filter : undefined,
                savedCategoriesTreeState: !action.payload.close && state.categoriesTreeState ? state.categoriesTreeState : initialState.categoriesTreeState,
                pagination: initialState.pagination,
                filter: action.payload.close ? state.savedFilter : undefined,
                categoriesTreeState: action.payload.close && state.savedCategoriesTreeState ? state.savedCategoriesTreeState : initialState.categoriesTreeState
            });
        } else {
            if (action.payload.close) {
                return adapter.setAll(action.payload.items, {
                    ...state,
                    pagination: {...state.pagination, number: 0},
                    componentOpened: false
                });
            }

            return {...state, componentOpened: true};
        }
    }),

    on(NomenclaturesActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(NomenclaturesActions.LoadListSuccess, (state, action) => {
        return action.payload.clearState
            ? adapter.setAll(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined

            })
            : adapter.upsertMany(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            });
    }),

    on(NomenclaturesActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(NomenclaturesActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(NomenclaturesActions.SetPagination, (state, action) => ({
        ...state,
        pagination: {...state.pagination, number: action.payload ? StoredComponentHelperService.setNextPage(state.pagination, state.ids?.length) : 0}
    })),

    on(NomenclaturesActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(NomenclaturesActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(NomenclaturesActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload, state?.categoriesTreeState);
        if (savedItemAvailableByFilter) {
            return adapter.setOne(action.payload!, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(NomenclaturesActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(NomenclaturesActions.DeleteItemSuccess, (state, action) => {
        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(NomenclaturesActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(NomenclaturesActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();
export const {selectAll: selectAllCategories} = categoriesAdapter.getSelectors((state: NomenclatureState) => state.entityCategories);

const isAvailableByFilter = (searchParams: NomenclatureSearchParams | undefined, item: Nomenclature, categoriesTreeState?: CategoriesTreeState): boolean => {
    if (categoriesTreeState?.selectedCategory?.id && categoriesTreeState.selectedCategory?.id != item.category?.id) {
        return false;
    }

    if (!searchParams?.includeDeleted && item.archived) {
        return false;
    }

    if (!searchParams) {
        return true;
    }

    if (searchParams.query && item.name && item.name.toLowerCase().indexOf(searchParams.query.toLowerCase()) == -1) {
        return false;
    }

    return true;
};
